/**
 * SEO + accessibility layer for the terminal site.
 *
 * The terminal is fully client-rendered and content only appears on command,
 * so search engines and screen readers would otherwise see an empty shell.
 * This renders a visually-hidden (.sr-only) mirror of the résumé with real
 * semantic headings, plus a JSON-LD Person graph. Everything is sourced from
 * src/content.ts — no duplicated copy to keep in sync.
 */

import { content } from "./content";

function personJsonLd(): string {
  const data = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: content.name,
    jobTitle: content.tagline,
    description: content.intro,
    url: "https://laureldaigneault.com",
    email: `mailto:${content.email}`,
    address: { "@type": "PostalAddress", addressLocality: content.location },
    sameAs: content.links
      .filter((l) => l.href.startsWith("http"))
      .map((l) => l.href),
    knowsAbout: content.skills.flatMap((g) => g.items),
    alumniOf: content.education.map((e) => ({
      "@type": "EducationalOrganization",
      name: e.institution,
    })),
    worksFor: content.experience
      .filter((e) => /present/i.test(e.period))
      .map((e) => ({ "@type": "Organization", name: e.company })),
  };
  return JSON.stringify(data);
}

export function Seo() {
  return (
    <>
      <script
        type="application/ld+json"
        // JSON.stringify output is safe to inline; no user input involved.
        dangerouslySetInnerHTML={{ __html: personJsonLd() }}
      />
      <div className="sr-only">
        <h1>{content.name}</h1>
        <p>{content.tagline}</p>
        <p>{content.intro}</p>

        <h2>About</h2>
        {content.about.map((p, i) => (
          <p key={i}>{p}</p>
        ))}

        <h2>Experience</h2>
        <ul>
          {content.experience.map((job) => (
            <li key={job.company}>
              <h3>
                {job.role} — {job.company}
              </h3>
              <p>
                {job.period}
                {job.location ? `, ${job.location}` : ""}
              </p>
              <ul>
                {job.highlights.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>

        <h2>Skills</h2>
        <ul>
          {content.skills.map((group) => (
            <li key={group.label}>
              <strong>{group.label}:</strong> {group.items.join(", ")}
            </li>
          ))}
        </ul>

        <h2>Projects</h2>
        <ul>
          {content.projects.map((proj) => (
            <li key={proj.name}>
              <h3>{proj.name}</h3>
              <p>{proj.blurb}</p>
              {proj.link ? <a href={proj.link}>{proj.link}</a> : null}
            </li>
          ))}
        </ul>

        <h2>Education</h2>
        <ul>
          {content.education.map((ed) => (
            <li key={ed.credential}>
              <h3>{ed.credential}</h3>
              <p>
                {ed.institution} — {ed.period}
              </p>
              {ed.detail ? <p>{ed.detail}</p> : null}
            </li>
          ))}
        </ul>

        <h2>Contact</h2>
        <p>
          <a href={`mailto:${content.email}`}>{content.email}</a>
        </p>
        <ul>
          {content.links.map((l) => (
            <li key={l.label}>
              <a href={l.href}>{l.label}</a>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
